import * as io from "socket.io-client";
import axios from "axios";
import { BaseURL, Endpoint } from "shared/utils/endpoints";
import { store } from "shared/redux/store";
import {
  setAuthReducer,
  resetAuthReducer,
} from "shared/redux/reducers/authSlice";
import { routeConstant } from "shared/routes/routeConstant";
import {Socket} from "socket.io-client";
import DisconnectReason = Socket.DisconnectReason;

let socket: io.Socket | null = null;
let currentUserId: number | null = null;
let isRefreshingToken = false;

// ─── Token Refresh (centralized, in-place reconnect) ──────────────────────────

const refreshTokenAndReconnect = async (retryCount = 0, maxRetries = 3) => {
  if (isRefreshingToken) return;
  isRefreshingToken = true;

  const { auth } = store.getState().root;
  const refreshToken = auth?.refreshToken;

  if (!refreshToken) {
    isRefreshingToken = false;
    store.dispatch(resetAuthReducer());
    redirectToLoginIfNotOnAuthRoute();
    return;
  }

  try {
    const response = await axios.post(
      `${BaseURL}${Endpoint.auth.refreshToken}`,
      { refreshToken },
      { timeout: 5000 }
    );

    const responseData = response.data?.data || response.data;
    const { accessToken, refreshToken: newRefreshToken, token } = responseData;
    const newAccessToken = accessToken || token;
    const newRefresh = newRefreshToken || refreshToken;

    if (!newAccessToken) {
      throw new Error('Invalid token response');
    }

    store.dispatch(setAuthReducer({ token: newAccessToken, refreshToken: newRefresh }));
    isRefreshingToken = false;

    // Update credentials IN PLACE on the existing socket instance — no recreation.
    // All hook references to the socket remain valid.
    if (socket) {
      (socket as any).auth = { token: newAccessToken, authorization: `Bearer ${newAccessToken}` };
      (socket.io as any).opts.query = { userId: currentUserId, token: newAccessToken };
      socket.connect();
    }
  } catch (error) {
    const isRetryable = isTemporaryError(error) && retryCount < maxRetries;

    if (isRetryable) {
      const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
      setTimeout(() => {
        refreshTokenAndReconnect(retryCount + 1, maxRetries);
      }, delay);
    } else {
      isRefreshingToken = false;
      store.dispatch(resetAuthReducer());
      redirectToLoginIfNotOnAuthRoute();
    }
  }
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isTemporaryError(error: any): boolean {
  if (!error.response) return true; // network error
  const status = error.response.status;
  return status >= 500;
}

function isAuthRelatedError(error: any): boolean {
  if (error?.data?.status === 401 || error?.data?.code === 401) return true;
  if (error?.status === 401) return true;
  const msg = (error?.message || "").toLowerCase();
  return (
    msg.includes("unauthorized") ||
    msg.includes("jwt expired") ||
    msg.includes("jwt malformed") ||
    msg.includes("invalid token") ||
    msg.includes("forbidden") ||
    msg.includes("authentication error")
  );
}

function redirectToLoginIfNotOnAuthRoute() {
  if (typeof window === 'undefined') return;
  const isAuthRoute = window.location.pathname.includes('/auth/');
  if (!isAuthRoute) {
    window.location.href = routeConstant.login.path;
  }
}

// ─── Init (called once on login; idempotent — safe to call on page refresh) ──

const initSocket = (userId: number): io.Socket | null => {
  // @ts-ignore
  const token = store.getState().root.auth?.token;

  if (!token) return null;

  currentUserId = userId;

  // Already initialized — return the same instance (never recreate mid-session)
  if (socket) return socket;

  socket = io.connect(BaseURL, {
    transports: ["websocket"],
    query: {
      userId: userId,
      token: token,
    },
    auth: {
      token: token,
      authorization: `Bearer ${token}`,
    },
    reconnection: true,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 300,
  });

  // All lifecycle listeners attached ONCE to this instance — never re-attached
  socket.on("connect", () => {
    isRefreshingToken = false;
  });

  socket.on("disconnect", (reason: DisconnectReason) => {
    if (reason === "transport close") {
      // Server is down / network blip.
      // Socket.IO auto-reconnects (300 attempts, 2-5s intervals) — do nothing.
    } else if (reason === "io server disconnect") {
      // Server actively disconnected us while UP — auth rejected server-side.
      // Refresh token and reconnect the same socket instance in-place.
      refreshTokenAndReconnect();
    }
    // "io client disconnect" — intentional logout via disconnectSocket(), do nothing.
  });

  socket.on("connect_error", (error: Error) => {
    if (isAuthRelatedError(error)) {
      // Server is up but rejected our token — refresh and reconnect in-place.
      refreshTokenAndReconnect();
    }
    // Network error (server still down) — Socket.IO auto-retries, do nothing.
  });

  socket.on("unauthorized", () => {
    refreshTokenAndReconnect();
  });

  return socket;
};

// ─── Disconnect (called once on logout) ───────────────────────────────────────

const disconnectSocket = () => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    currentUserId = null;
    isRefreshingToken = false;
  }
};

// ─── Accessor ─────────────────────────────────────────────────────────────────

const getSocket = () => {
  return socket;
};

export { initSocket, getSocket, disconnectSocket };
